import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Trash2, RotateCcw, AlertTriangle, RefreshCw, Search, Filter,
  CheckCircle2, XCircle, Activity, Database, ShieldAlert, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const MODULES = [
  { id: 'all',         label: 'All Modules' },
  // ── Phase 1A ──
  { id: 'leads',       label: 'Leads' },
  { id: 'bookings',    label: 'Bookings' },
  { id: 'customers',   label: 'Customers (CRM)' },
  { id: 'customer_accounts', label: 'Customer Accounts (Website)' },
  { id: 'invoices',    label: 'Invoices' },
  { id: 'payments',    label: 'Payments' },
  // ── Phase 1B ──
  { id: 'coupons',     label: 'Coupons' },
  { id: 'employees',   label: 'Employees' },
  { id: 'reviews',     label: 'Reviews' },
  { id: 'wa_templates', label: 'WhatsApp Templates' },
  { id: 'inventory',   label: 'Inventory' },
  { id: 'amc',         label: 'AMC Contracts' },
  { id: 'tickets',     label: 'Service Tickets' },
  { id: 'quotations',  label: 'Quotations' },
  { id: 'warranties',  label: 'Warranties' },
];

function StatCard({ label, value, color, Icon }) {
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${color} text-white shadow-sm`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] uppercase tracking-wider font-semibold opacity-90">{label}</span>
        <Icon size={16} className="opacity-80" />
      </div>
      <div className="text-2xl font-bold">{value ?? '—'}</div>
    </div>
  );
}

// Permanent-delete confirmation modal — requires typing "DELETE"
function PermDeleteDialog({ items, onClose, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [working, setWorking] = useState(false);
  const valid = typed.trim() === 'DELETE';
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="recycle-perm-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <ShieldAlert size={20} /> Permanent Delete
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">
            <p className="font-bold mb-1">This action cannot be undone.</p>
            <p>
              You are about to permanently delete <strong>{items.length}</strong> record
              {items.length === 1 ? '' : 's'} from the database. A snapshot will be kept in the
              backup vault for emergency restoration.
            </p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">
              Type <code className="bg-slate-100 px-1.5 rounded text-red-700 font-bold">DELETE</code> to confirm:
            </label>
            <Input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="DELETE"
              data-testid="perm-confirm-input"
              className="font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={async () => { setWorking(true); try { await onConfirm(); } finally { setWorking(false); } }}
            disabled={!valid || working}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            data-testid="perm-confirm-btn"
          >
            {working ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
            Permanently Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecycleBinTab({ api, currentUser }) {
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [view, setView] = useState('items'); // items | audit
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [permDialog, setPermDialog] = useState(null); // { ids[], module }

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (moduleFilter !== 'all') q.set('module', moduleFilter);
      if (search) q.set('search', search);
      const [it, st, au] = await Promise.all([
        api('GET', `/admin/recycle-bin?${q.toString()}`),
        api('GET', '/admin/recycle-bin/stats'),
        api('GET', '/admin/recycle-bin/audit?limit=100'),
      ]);
      setItems(it.data?.items || []);
      setStats(st.data || {});
      setAudit(au.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Recycle Bin');
    } finally {
      setLoading(false);
    }
  }, [api, moduleFilter, search]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleSel = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const selectAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => `${i.__module__}::${i.id}`)));
  };

  const bulkAction = async (action) => {
    const byModule = {};
    for (const key of selected) {
      const [mod, id] = key.split('::');
      (byModule[mod] = byModule[mod] || []).push(id);
    }
    if (Object.keys(byModule).length === 0) return;

    if (action === 'permanent_delete') {
      setPermDialog({ groups: byModule });
      return;
    }
    try {
      for (const [mod, ids] of Object.entries(byModule)) {
        await api('POST', '/admin/recycle-bin/bulk', { module: mod, ids, action });
      }
      toast.success(`Bulk ${action.replace('_', ' ')} done`);
      setSelected(new Set());
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const doPermBulk = async () => {
    try {
      for (const [mod, ids] of Object.entries(permDialog.groups)) {
        await api('POST', '/admin/recycle-bin/bulk', {
          module: mod, ids, action: 'permanent_delete', confirm: 'DELETE',
        });
      }
      toast.success('Permanently deleted (backups stored)');
      setPermDialog(null);
      setSelected(new Set());
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const restoreOne = async (item) => {
    try {
      await api('POST', `/admin/recycle-bin/${item.__module__}/${item.id}/restore`);
      toast.success('Restored');
      await loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const permOne = (item) => setPermDialog({ groups: { [item.__module__]: [item.id] } });

  const filteredItems = useMemo(() => items, [items]);

  return (
    <div data-testid="recycle-bin-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Trash2 size={22} className="text-red-600" /> Recycle Bin
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Safety net for accidentally deleted records · {isSuperAdmin ? <Badge className="bg-purple-100 text-purple-700 border-purple-200 ml-1">SUPER ADMIN</Badge> : <Badge className="bg-amber-100 text-amber-700 border-amber-200 ml-1">ADMIN</Badge>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadAll} data-testid="rb-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Dashboard widgets */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        <StatCard label="Active Records" value={stats.active_total} color="from-emerald-500 to-emerald-700" Icon={Database} />
        <StatCard label="In Recycle Bin" value={stats.deleted_total} color="from-red-500 to-red-700" Icon={Trash2} />
        <StatCard label="Deleted (24h)" value={stats.recently_deleted_24h} color="from-amber-500 to-amber-700" Icon={AlertTriangle} />
        <StatCard label="Restored (24h)" value={stats.recently_restored_24h} color="from-blue-500 to-blue-700" Icon={RotateCcw} />
        <StatCard label="Permanent (24h)" value={stats.recently_permanent_24h} color="from-slate-700 to-slate-900" Icon={ShieldAlert} />
      </div>

      {/* Sub-nav */}
      <div className="flex gap-1 border-b border-slate-200 mb-4">
        <button
          onClick={() => setView('items')}
          data-testid="rb-subnav-items"
          className={`px-4 py-2 text-sm font-semibold border-b-2 ${view === 'items' ? 'border-red-600 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        ><Trash2 size={14} className="inline mr-1.5" /> Items ({items.length})</button>
        <button
          onClick={() => setView('audit')}
          data-testid="rb-subnav-audit"
          className={`px-4 py-2 text-sm font-semibold border-b-2 ${view === 'audit' ? 'border-red-600 text-red-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        ><Activity size={14} className="inline mr-1.5" /> Audit Log ({audit.length})</button>
      </div>

      {view === 'items' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-3">
            <select
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm"
              data-testid="rb-module-filter"
            >
              {MODULES.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} {m.id !== 'all' && stats.deleted_by_module ? `(${stats.deleted_by_module[m.id] || 0})` : ''}
                </option>
              ))}
            </select>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search by name, phone, ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadAll()}
                className="pl-8 h-9 w-56"
                data-testid="rb-search"
              />
            </div>
            <Button size="sm" variant="outline" onClick={loadAll}><Filter size={13} className="mr-1.5" /> Apply</Button>

            {/* Bulk actions */}
            {selected.size > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-slate-600 font-semibold">{selected.size} selected</span>
                <Button size="sm" variant="outline" onClick={() => bulkAction('restore')} data-testid="rb-bulk-restore">
                  <RotateCcw size={13} className="mr-1" /> Restore
                </Button>
                {isSuperAdmin && (
                  <Button size="sm" onClick={() => bulkAction('permanent_delete')} className="bg-red-600 hover:bg-red-700" data-testid="rb-bulk-perm">
                    <Trash2 size={13} className="mr-1" /> Permanent
                  </Button>
                )}
              </div>
            )}
          </div>

          {loading && <div className="text-sm text-slate-500 mb-2"><Loader2 size={14} className="animate-spin inline mr-1" /> Loading…</div>}

          {filteredItems.length === 0 && !loading ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <CheckCircle2 size={36} className="mx-auto text-emerald-400 mb-2" />
              <p className="text-base font-semibold text-slate-700 mb-1">Recycle Bin is empty</p>
              <p className="text-sm text-slate-500">No deleted records match your filters.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">
                      <input
                        type="checkbox"
                        checked={selected.size > 0 && selected.size === items.length}
                        onChange={selectAll}
                        data-testid="rb-select-all"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Module</th>
                    <th className="px-3 py-2 text-left">Record</th>
                    <th className="px-3 py-2 text-left">Deleted At</th>
                    <th className="px-3 py-2 text-left">Deleted By</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.map((item) => {
                    const key = `${item.__module__}::${item.id}`;
                    return (
                      <tr key={key} data-testid={`rb-row-${item.id}`} className="hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selected.has(key)} onChange={() => toggleSel(key)} />
                        </td>
                        <td className="px-3 py-2">
                          <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] uppercase">{item.__module__}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-semibold text-slate-900">{item.name || item.customer_name || item.booking_code || item.invoice_no || item.id}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{item.id}</div>
                          {item.phone && <div className="text-[11px] text-slate-500">{item.phone}</div>}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-500">
                          {item.deleted_at ? new Date(item.deleted_at).toLocaleString() : '—'}
                        </td>
                        <td className="px-3 py-2 text-[11px] text-slate-600">{item.deleted_by_email || '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => restoreOne(item)} className="mr-1.5" data-testid={`rb-restore-${item.id}`}>
                            <RotateCcw size={12} className="mr-1" /> Restore
                          </Button>
                          {isSuperAdmin && (
                            <Button size="sm" onClick={() => permOne(item)} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`rb-perm-${item.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === 'audit' && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {audit.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No audit entries yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Action</th>
                  <th className="px-3 py-2 text-left">Module</th>
                  <th className="px-3 py-2 text-left">Record ID</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {audit.map((a) => (
                  <tr key={a.id} data-testid={`rb-audit-${a.id}`} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-[11px] text-slate-500">{a.at ? new Date(a.at).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">
                      <Badge className={
                        a.action.includes('restore') ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                        a.action.includes('permanent') ? 'bg-red-100 text-red-700 border-red-200' :
                        'bg-amber-100 text-amber-700 border-amber-200'
                      }>{a.action}</Badge>
                    </td>
                    <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-600">{a.module}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{a.record_id}</td>
                    <td className="px-3 py-2 text-[11px]">{a.user_email} <span className="text-slate-400">({a.user_role})</span></td>
                    <td className="px-3 py-2 text-[11px] font-mono text-slate-500">{a.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {permDialog && (
        <PermDeleteDialog
          items={Object.values(permDialog.groups).flat()}
          onClose={() => setPermDialog(null)}
          onConfirm={doPermBulk}
        />
      )}
    </div>
  );
}
